import React from "react";
import { Controller, ControllerRenderProps, useFormContext, useWatch } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";
import { useQuery } from "@apollo/client";

import { Checkbox, FormGroup, Input } from "@cofano/cck";

import { Button } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import ApplicationLanguageSelect from "components/selects/ApplicationLanguageSelect";
import NumberInput from "components/cck/NumberInput";

import { Fields, hasOptionalFieldPermission, hasPluginPermission, Language } from "imports/Utils";

import { RelationSchemaInput, RelationSchemaOutput } from "view/relations/utils/relation.schema";
import RelationProductGroupsSelect from "view/relations/components/selects/RelationProductGroupsSelect";

import { GetMaxFileSizeDocument } from "__generated__/view/admin/graphql/admin.queries.generated";

const RelationDetailsForm = () => {

	const { control, setValue, watch } = useFormContext<RelationSchemaInput, unknown, RelationSchemaOutput>();

	const session = useStoreState(store => store.session);

	const { data: { maxFileSize } = { maxFileSize: 0 } } = useQuery(GetMaxFileSizeDocument);

	const types = watch([
		"isCustomer",
		"isSupplier",
		"isBroker",
		"isSurveyor",
		"isOperator",
		"isShipManager",
		"isSkipper",
		"isShippingCompany",
		"isApplicationRelation",
		"isLocationRelation",
		"isAgent",
		"isConsignor",
		"isContainerOperator",
	]);

	const [
		isCustomer,
		_isSupplier,
		_isBroker,
		_isSurveyor,
		isOperator,
		isShipManager,
		isSkipper,
		isShippingCompany,
		isApplicationRelation,
		isLocationRelation,
		_isAgent,
		_isConsignor,
		_isContainerOperator,
	] = types;

	const [ id ] = useWatch({ control, name: [ "id" ] });

	const customerProductGroups = session?.settings?.multiValues?.customerProductGroups;

	const isRelationTypeDisabled = (field: ControllerRenderProps<RelationSchemaInput, keyof RelationSchemaInput>): boolean => {

		const amountOfTypesEnabled = [ ...types ].filter(boolean => boolean).length;

		if ((field.name === "isCustomer" || field.name === "isSupplier" || field.name === "isOperator") && hasPluginPermission(session, "externalRelationManagement")) {
			return true;
		} else if (field.name === "isOperator") {
			return isSkipper || isShipManager || false;
		} else if (field.name === "isShipManager") {
			return isSkipper || isOperator || false;
		} else {
			return amountOfTypesEnabled == 1 && field.value === true;
		}
	};

	return (
		<React.Fragment>
			{hasOptionalFieldPermission(session, Fields.RELATION, "customerNumber") && isCustomer && (
				<FormGroup label={Language("CLIENT_NUMBER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="customerNumber"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={0} disabled={!!id} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			<FormGroup
				label={Language("CLIENT_CODE")}
				labelPlacement="left-center"
				overflow="hidden"
				required>
				<Controller
					name="name"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup label={Language("EMAIL")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="email"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup label={Language("CLIENT_LANGUAGE")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="language"
					control={control}
					render={({ field }) => <ApplicationLanguageSelect {...field} clearable={false} />} />
			</FormGroup>

			<FormGroup label={Language("CLIENT_ABBREVIATION")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="abbreviation"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			{isShippingCompany && (
				<FormGroup label={Language("CLIENT_SCAC")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="scac"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "ean") && isOperator && (
				<FormGroup label={Language("OPERATORS_EAN")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="ean"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{customerProductGroups && isCustomer && (
				<FormGroup label={Language("CLIENT_PRODUCT_GROUP")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="productGroup"
						control={control}
						render={({ field }) => <RelationProductGroupsSelect {...field} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "externalCode") && (isCustomer || isOperator || isApplicationRelation) && (
				<FormGroup label={Language("EXTERNAL_CODE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="externalCode"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "externalCustomerNumber") && (isCustomer || isOperator) && (
				<FormGroup label={Language("EXTERNAL_CUSTOMER_NUMBER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="externalCustomerNumber"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "organisation") && isCustomer && (
				<FormGroup label={Language("CLIENT_ORGANISATION")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="organisation"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "telephone") && (
				<FormGroup label={Language("PHONE_NUMBER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="telephone"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "volumePerYear") && isCustomer && (
				<FormGroup label={Language("CLIENT_VOLUME_A_YEAR")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="volumePerYear"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} decimalScale={0} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "transportFrequency") && isCustomer && (
				<FormGroup label={Language("CLIENT_TRANSPORT_FREQUENCY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="transportFrequency"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			<FormGroup label={Language("CLIENT_SINGLE")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="isCustomer"
					control={control}
					render={({ field }) => (
						<Checkbox
							{...field}
							value={undefined}
							checked={field.value}
							disabled={isRelationTypeDisabled(field)} />
					)} />
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.ORDER, "supplier") && (
				<FormGroup label={Language("SUPPLIER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isSupplier"
						control={control}
						render={({ field }) => (
							<Checkbox
								{...field}
								value={undefined}
								checked={field.value}
								disabled={isRelationTypeDisabled(field)} />
						)} />
				</FormGroup>
			)}

			{!isLocationRelation && (
				<FormGroup label={Language("OPERATOR")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isOperator"
						control={control}
						render={({ field }) => (
							<Checkbox
								{...field}
								value={undefined}
								checked={field.value}
								disabled={isRelationTypeDisabled(field)} />
						)} />
				</FormGroup>
			)}

			<FormGroup label={Language("SHIP_MANAGER")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="isShipManager"
					control={control}
					render={({ field }) => (
						<Checkbox
							{...field}
							value={undefined}
							checked={field.value}
							disabled={isRelationTypeDisabled(field)} />
					)} />
			</FormGroup>

			<FormGroup
				label={Language("SKIPPER")}
				labelPlacement="left-center"
				overflow="hidden"
				tooltip={intl.formatMessage({ id: "SKIPPER_RELATIONS_ARE_MANAGED_BY_THE_SYSTEM" })}
			>
				<Controller
					name="isSkipper"
					control={control}
					render={({ field }) => (
						<Checkbox
							{...field}
							value={undefined}
							checked={field.value}
							disabled
						/>
					)} />
			</FormGroup>

			{hasPluginPermission(session, "shippingCompanies") && !isLocationRelation && (
				<FormGroup label={Language("SHIPPING_COMPANY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isShippingCompany"
						control={control}
						render={({ field }) => (
							<Checkbox
								{...field}
								value={undefined}
								checked={field.value}
								disabled={isRelationTypeDisabled(field)} />
						)} />
				</FormGroup>
			)}

			<FormGroup label={Language("APPLICATION_RELATION")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="isApplicationRelation"
					control={control}
					render={({ field }) => (
						<Checkbox
							{...field}
							value={undefined}
							checked={field.value}
							disabled={isRelationTypeDisabled(field)} />
					)} />
			</FormGroup>

			{hasPluginPermission(session, "locationRelations") && (
				<FormGroup label={Language("LOCATION_MANAGER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isLocationRelation"
						control={control}
						render={({ field }) => (
							<Checkbox
								{...field}
								value={undefined}
								onChange={(event) => {
									field.onChange(event.target.checked);

									if (event.target.checked) {
										setValue("isOperator", false);
										setValue("isShippingCompany", false);
									}
								}}
								checked={field.value}
								disabled={isRelationTypeDisabled(field)} />
						)} />
				</FormGroup>
			)}

			{hasPluginPermission(session, "agents") && (
				<FormGroup label={Language("ORDER_AGENT")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isAgent"
						control={control}
						render={({ field }) => (
							<Checkbox
								{...field}
								value={undefined}
								checked={field.value}
								disabled={isRelationTypeDisabled(field)} />
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "isConsignor") && (
				<FormGroup label={Language("CLIENT_IS_CONSIGNOR")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isConsignor"
						control={control}
						render={({ field }) => (
							<Checkbox
								{...field}
								value={undefined}
								checked={field.value}
								disabled={isRelationTypeDisabled(field)} />
						)} />
				</FormGroup>
			)}

			{hasPluginPermission(session, "containerOperators") &&  (
				<FormGroup label={Language("CONTAINER_OPERATORS")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isContainerOperator"
						control={control}
						render={({ field }) => (
							<Checkbox
								{...field}
								value={undefined}
								checked={field.value}
								disabled={isRelationTypeDisabled(field)} />
						)} />
				</FormGroup>
			)}

			{hasPluginPermission(session, "brokers") &&  (
				<FormGroup label={Language("BROKER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isBroker"
						control={control}
						render={({ field }) => (
							<Checkbox
								{...field}
								value={undefined}
								checked={field.value}
								disabled={isRelationTypeDisabled(field)} />
						)} />
				</FormGroup>
			)}

			{hasPluginPermission(session, "surveyors") &&  (
				<FormGroup label={Language("SURVEYOR")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isSurveyor"
						control={control}
						render={({ field }) => (
							<Checkbox
								{...field}
								value={undefined}
								checked={field.value}
								disabled={isRelationTypeDisabled(field)} />
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "maxCargoDeviationForProtest") && (
				<FormGroup label={Language("MAX_CARGO_DEVIATION_FOR_LETTER_PROTEST")} labelPlacement="left-center" overflow="visible">
					<Controller
						name="maxCargoDeviationForLetterOfProtest"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} addonAfter={"%"} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "maxCargoDeviationForProtest") && (
				<FormGroup label={Language("MAX_CARGO_DEVIATION_FOR_CALL_KWKW")} labelPlacement="left-center" overflow="visible">
					<Controller
						name="maxCargoDeviationForCallKWKW"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} addonAfter={"%"} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "roundVatEachLine") && id && (
				<FormGroup label={Language("ROUND_VAT_EACH_LINE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="roundVatEachLine"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "termsAndConditions") && isApplicationRelation && (
				<FormGroup label={Language("TERMS_AND_CONDITIONS_PURCHASE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="termsAndConditionsPurchase"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "termsAndConditions") && isApplicationRelation && (
				<FormGroup label={Language("TERMS_AND_CONDITIONS_SALE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="termsAndConditionsSale"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			<FormGroup label={Language("LOGO")} labelPlacement="left" overflow="hidden">
				<Controller
					name="logo"
					control={control}
					render={({ field }) => (
						<React.Fragment>
							<Button
								component="label"
								variant="contained"
								startIcon={<CloudUploadIcon />}
								size="small">
								{Language("UPLOAD_FILE")}

								<VisuallyHiddenInput
									type="file"
									onChange={event => {
										const file = event.target.files?.[0];

										if (file) {
											if (file.size > (maxFileSize * 1000000)) {
												alert(intl.formatMessage({ id: "FILE_TO_LARGE" }).replace("%d", (maxFileSize * 1000000).toString()));
											} else {
												const reader = new FileReader();
												reader.onloadend = () => {
													if (typeof reader.result === "string") {
														field.onChange(reader.result?.split(",")?.[1]);
													}
												};
												reader.readAsDataURL(file);
											}
										} else {
											field.onChange(null);
										}
									}} />
							</Button>

							{field.value && (
								<div>
									<img
										alt="Logo"
										style={{ marginTop: 16, maxWidth: "100%" }}
										src={`data:image/jpg|png|jpeg;base64,${field.value}`} />
								</div>
							)}
						</React.Fragment>
					)} />
			</FormGroup>
		</React.Fragment>
	);
};

const VisuallyHiddenInput = styled("input")({
	clip: "rect(0 0 0 0)",
	clipPath: "inset(50%)",
	height: 1,
	overflow: "hidden",
	position: "absolute",
	bottom: 0,
	left: 0,
	whiteSpace: "nowrap",
	width: 1,
});

export default RelationDetailsForm;
